import { useState, useRef, useEffect } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, FileSpreadsheet, Download, Check, X, Wand2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function ImportExportPage() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/accounts").then(r => { setAccounts(r.data); if (r.data[0]) setAccountId(r.data[0].id); });
    api.get("/scheduler-settings").then(r => setScheduler(r.data)).catch(() => {});
  }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/csv-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.row_count} Zeilen erkannt`);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const commit = async () => {
    if (!accountId) { toast.error("Bitte Konto wählen"); return; }
    setBusy(true);
    try {
      const accepted = preview.rows.filter(r => r._accept !== false);
      const { data } = await api.post("/csv-import/commit", { account_id: accountId, rows: accepted, auto_match: true });
      toast.success(`✓ ${data.customer_payments} Kundenzahlungen, ${data.invoice_payments} Lieferantenzahlungen erfasst (übersprungen: ${data.skipped})`);
      setPreview(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    setBusy(false);
  };

  const toggleRow = (idx) => {
    const rows = preview.rows.map(r => r.row_index === idx ? { ...r, _accept: r._accept === false ? true : false } : r);
    setPreview({ ...preview, rows });
  };

  const downloadDatev = async () => {
    try {
      const resp = await api.get("/datev-export", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "datev-buchungsstapel.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("DATEV-Export fehlgeschlagen"); }
  };

  const saveScheduler = async () => {
    try {
      await api.put("/scheduler-settings", scheduler);
      toast.success("Scheduler gespeichert");
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const recalcStages = async () => {
    try {
      const { data } = await api.post("/dunning/recalculate-stages");
      toast.success(`${data.updated} Mahnstufen aktualisiert`);
    } catch (e) { toast.error("Fehler"); }
  };

  const matched = preview?.rows.filter(r => r.suggested_match).length || 0;

  return (
    <div className="space-y-6" data-testid="import-export-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Import / Export & Automatisierung</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">CSV-Import, DATEV & Scheduler</h1>
        <p className="text-sm text-neutral-500 mt-2">Bank-Kontoauszüge per CSV einlesen, DATEV-Buchungsstapel für deinen Steuerberater exportieren und Automatik-Jobs steuern.</p>
      </div>

      {/* CSV Import */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="csv-import-block">
        <div className="flex items-center gap-3 mb-4">
          <FileSpreadsheet className="h-5 w-5" />
          <h2 className="font-display text-lg font-semibold">CSV-Import (Bank / Stripe / PayPal)</h2>
        </div>
        {!preview ? (
          <div className="space-y-3">
            <p className="text-sm text-neutral-600">Lade einen Kontoauszug oder Stripe/PayPal-Export hoch. AgencyCash erkennt das Format automatisch und schlägt passende offene Rechnungen vor.</p>
            <div className="flex gap-3 items-center flex-wrap">
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="w-72" data-testid="csv-account"><SelectValue placeholder="Konto wählen" /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
              <input ref={fileRef} type="file" hidden accept=".csv,.txt" onChange={onUpload} data-testid="csv-file" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy || !accountId} data-testid="csv-upload-btn"><Upload className="h-4 w-4 mr-2" />CSV hochladen</Button>
            </div>
            <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg p-3">
              💡 Unterstützte Formate: DKB, N26, Sparkasse SEPA, Stripe Payouts, PayPal Aktivität.<br />
              Positive Beträge → Kundenzahlung · Negative Beträge → Lieferantenzahlung. Auto-Match anhand von Betrag und Rechnungsnummer im Verwendungszweck.
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="csv-preview">
            <div className="text-sm">
              <strong>{preview.row_count}</strong> Zeilen · <strong className="text-emerald-700">{matched}</strong> automatisch zugeordnet
            </div>
            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow><TableHead></TableHead><TableHead>Datum</TableHead><TableHead>Beschreibung</TableHead><TableHead>Gegenkonto</TableHead><TableHead className="text-right">Betrag</TableHead><TableHead>Vorschlag</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.row_index} className={r._accept === false ? "opacity-40" : ""} data-testid={`csv-row-${r.row_index}`}>
                      <TableCell><input type="checkbox" checked={r._accept !== false} onChange={() => toggleRow(r.row_index)} /></TableCell>
                      <TableCell className="text-xs text-neutral-600 whitespace-nowrap">{fmtDate(r.booking_date)}</TableCell>
                      <TableCell className="text-sm max-w-md truncate">{r.description}</TableCell>
                      <TableCell className="text-xs text-neutral-500">{r.counterparty}</TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${r.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtCurrency(r.amount)}</TableCell>
                      <TableCell>
                        {r.suggested_match ? (
                          <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">
                            <Wand2 className="h-3 w-3 mr-1" />
                            {r.suggested_match.type === "outgoing_invoice" ? r.suggested_match.invoice_number : r.suggested_match.supplier_name}
                          </Badge>
                        ) : <span className="text-xs text-neutral-400">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex gap-2">
              <Button onClick={commit} disabled={busy} data-testid="csv-commit"><Check className="h-4 w-4 mr-2" />Buchen ({preview.rows.filter(r => r._accept !== false).length} Zeilen)</Button>
              <Button variant="ghost" onClick={() => setPreview(null)} data-testid="csv-cancel"><X className="h-4 w-4 mr-2" />Abbrechen</Button>
            </div>
          </div>
        )}
      </div>

      {/* DATEV Export */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="datev-block">
        <div className="flex items-center gap-3 mb-2">
          <Download className="h-5 w-5" />
          <h2 className="font-display text-lg font-semibold">DATEV-Buchungsstapel für Steuerberater</h2>
        </div>
        <p className="text-sm text-neutral-500 mb-4">Exportiert alle Ein- und Ausgangsrechnungen als DATEV-CSV (SKR04-Standardkonten). Direkt importierbar im DATEV Rechnungswesen / Unternehmen online.</p>
        <Button variant="outline" onClick={downloadDatev} data-testid="datev-download"><Download className="h-4 w-4 mr-2" />datev-buchungsstapel.csv herunterladen</Button>
      </div>

      {/* Scheduler */}
      {scheduler && (
        <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="scheduler-block">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="h-5 w-5" />
            <h2 className="font-display text-lg font-semibold">Automatisierung</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between border rounded-lg px-4 py-3">
              <div>
                <Label className="m-0 font-medium">IMAP automatisch synchronisieren</Label>
                <p className="text-xs text-neutral-500 mt-1">Holt alle X Minuten neue Belege aus deinen Postfächern.</p>
              </div>
              <Switch checked={!!scheduler.imap_sync_enabled} onCheckedChange={(v) => setScheduler({ ...scheduler, imap_sync_enabled: v })} data-testid="sched-imap-toggle" />
            </div>
            <div className={`pl-4 ${scheduler.imap_sync_enabled ? "" : "opacity-50 pointer-events-none"}`}>
              <Label>Intervall (Minuten)</Label>
              <Input type="number" min="5" max="240" value={scheduler.imap_sync_interval_min} onChange={(e) => setScheduler({ ...scheduler, imap_sync_interval_min: Number(e.target.value) })} className="max-w-xs" data-testid="sched-imap-interval" disabled={!scheduler.imap_sync_enabled} />
            </div>

            <div className="flex items-center justify-between border rounded-lg px-4 py-3">
              <div>
                <Label className="m-0 font-medium">Mahnstufen automatisch eskalieren</Label>
                <p className="text-xs text-neutral-500 mt-1">Setzt Mahnstufen je nach Tagen seit Fälligkeit automatisch hoch.</p>
              </div>
              <Switch checked={!!scheduler.dunning_auto_enabled} onCheckedChange={(v) => setScheduler({ ...scheduler, dunning_auto_enabled: v })} data-testid="sched-dunning-toggle" />
            </div>
            <div className="grid grid-cols-3 gap-4 pl-4">
              <div>
                <Label>Stufe 1 (Erinnerung) ab Tag</Label>
                <Input type="number" value={scheduler.dunning_stage1_days} onChange={(e) => setScheduler({ ...scheduler, dunning_stage1_days: Number(e.target.value) })} data-testid="sched-s1" />
              </div>
              <div>
                <Label>Stufe 2 (Mahnung) ab Tag</Label>
                <Input type="number" value={scheduler.dunning_stage2_days} onChange={(e) => setScheduler({ ...scheduler, dunning_stage2_days: Number(e.target.value) })} data-testid="sched-s2" />
              </div>
              <div>
                <Label>Stufe 3 (Letzte/Inkasso) ab Tag</Label>
                <Input type="number" value={scheduler.dunning_stage3_days} onChange={(e) => setScheduler({ ...scheduler, dunning_stage3_days: Number(e.target.value) })} data-testid="sched-s3" />
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Button onClick={saveScheduler} data-testid="sched-save">Speichern</Button>
              <Button variant="outline" onClick={recalcStages} data-testid="sched-recalc">Mahnstufen jetzt neu berechnen</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
